import React from "react";
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";

export const RiskHeatmap: React.FC<{ riskMatrixDetails: any }> = ({
    riskMatrixDetails,
}) => {
    // Probability (Columns) → 1 to 5
    const probabilityLabels = [
        { value: 1, label: "Very Low" },
        { value: 2, label: "Low" },
        { value: 3, label: "Medium" },
        { value: 4, label: "High" },
        { value: 5, label: "Very High" },
    ];

    // Impact (Rows) → 5 to 1 (descending)
    const impactLabels = [
        { value: 5, label: "Very High" },
        { value: 4, label: "High" },
        { value: 3, label: "Medium" },
        { value: 2, label: "Low" },
        { value: 1, label: "Very Low" },
    ];

    const getColor = (score: number): string => {
        if (score <= 4) return "success.main";
        if (score <= 8) return "warning.light";
        if (score <= 12) return "warning.main";
        if (score <= 16) return "warning.dark";
        return "error.dark";
    };

    return (
        <Box sx={{ p: 4 }}>
            <Table
                sx={{
                    borderCollapse: "separate",
                    borderSpacing: "10px",
                }}
            >
                {/* ---- HEADER (Probability) ---- */}
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ border: "none" }} />
                        {probabilityLabels.map((prob) => (
                            <TableCell
                                key={prob.value}
                                align="center"
                                sx={{ fontWeight: "bold", border: "none" }}
                            >
                                {prob.label}
                            </TableCell>
                        ))}
                        <TableCell sx={{ border: "none" }} />
                    </TableRow>
                </TableHead>

                {/* ---- BODY (Impact) ---- */}
                <TableBody>
                    {/* Vertical label on the left */}
                    <TableCell
                        sx={{
                            fontWeight: "bold",
                            border: "none",
                            whiteSpace: "nowrap",
                            writingMode: "vertical-rl",
                            transform: "rotate(180deg)",
                            textAlign: "center",
                        }}
                        rowSpan={6}
                    >
                        Impact →
                    </TableCell>

                    {[...riskMatrixDetails]
                        .sort((a, b) => b.impact - a.impact)
                        .map((impactRow: any, index: number) => (
                            <TableRow key={impactRow.impact}>
                                {impactRow.risks.map(
                                    (
                                        cell: { score: number; risks: number; score_status: string },
                                        colIndex: number
                                    ) => (
                                        <TableCell
                                            key={colIndex}
                                            align="center"
                                            sx={{
                                                px: 1,
                                                py: 0.5,
                                                bgcolor: getColor(cell.score),
                                                color: "#fff",
                                                borderRadius: "6px",
                                                transition: "transform 0.2s",
                                                "&:hover": {
                                                    transform: "scale(1.05)",
                                                    cursor: "pointer",
                                                },
                                            }}
                                        >
                                            <Box
                                                py={1}
                                                display={"flex"}
                                                justifyContent={"space-between"}
                                                px={2}
                                                alignItems={"center"}
                                            >
                                                <Typography
                                                    fontWeight="bold"
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        borderStyle: "solid",
                                                        borderWidth: 1,
                                                        borderColor: "warning.contrastText",
                                                        borderRadius: "50%",
                                                        width: 30,
                                                        height: 30,
                                                        textAlign: "center",
                                                        fontSize: 12,
                                                    }}
                                                >
                                                    {cell.score}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ textDecoration: "underline" }}
                                                >
                                                    {cell.risks} Risks
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    )
                                )}
                                <TableCell
                                    sx={{
                                        border: "none",
                                        fontWeight: "bold",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {impactLabels.find((i) => i.value === impactRow.impact)?.label}
                                </TableCell>
                            </TableRow>
                        ))}

                    {/* ---- Footer ---- */}
                    <TableRow>
                        <TableCell sx={{ border: "none" }} />
                        <TableCell
                            colSpan={probabilityLabels.length}
                            align="center"
                            sx={{ fontWeight: "bold", border: "none" }}
                        >
                            Probability →
                        </TableCell>
                        <TableCell sx={{ border: "none" }} />
                    </TableRow>
                </TableBody>
            </Table>
        </Box>
    );
};